import { Pressable, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import type {
  TargetActualStats,
  TargetAxis,
  TargetMetric,
} from "@heraion/shared";

import { Chip } from "../primitives/Chip";
import { MiniTrendBars } from "../primitives/MiniTrendBars";
import { TextField } from "../primitives/TextField";
import { Icon } from "../Icon/Icon";
import { Select, type SelectOption } from "../ProgramEditor/Select";
import type { EditorStyles } from "../ProgramEditor/styles";
import { webTapFloor, type UiTheme } from "../theme";
import type { ComposerStyles } from "./composerStyles";
import type { PlanStyles } from "./planComposerStyles";

// AP-1b — the expanded, edit-in-place editor for ONE weekly target, split out
// of WeeklyTargetsSection (file-per-render-mode, like ProgramExerciseEditor).
// Purely presentational: the section owns all vocab/uniqueness/mutation and the
// reference-stats compute, and hands this component ready-made chip descriptors,
// the key options, the resolved unit/band strings, and the athlete's current
// weekly `actual` for the selected target. Reuses ps.editorFrame/editorHead/
// editorTitle/collapseBtn + the shared Chip / Select / TextField primitives.

type ChipDesc<K> = { key: K; label: string; selected: boolean; disabled: boolean };

type Props = {
  theme: UiTheme;
  cs: ComposerStyles;
  ps: PlanStyles;
  selectStyles: EditorStyles;
  title: string;
  axis: TargetAxis;
  axisChips: ChipDesc<TargetAxis>[];
  metricChips: ChipDesc<TargetMetric>[];
  keyValue: string;
  keyOptions: SelectOption[];
  valueText: string;
  unitLabel: string;
  // Resolved band label (±10% or −x%/+y%), for the field caption.
  bandLabel: string;
  // Band editing buffers (AP-1b-b): percentages below/above the goal.
  belowText: string;
  aboveText: string;
  scored: boolean;
  // The athlete's own weekly actual for this target (reference). Null when the
  // host didn't wire history (web) or for a legacy pattern target.
  actual?: TargetActualStats | null;
  // AP-7: average sets/week the PROGRAM prescribes for this muscle, so the goal
  // reads against what the program asks, not just what the athlete does. Null
  // for non-muscle/non-hard-set targets or when the host didn't wire it.
  prescribed?: number | null;
  // Format a reference number (canonical) the SAME way the meter shows it —
  // locale-grouped, tonnage compacted + unit-converted. So the reference reads
  // identically to the goal it seeds.
  formatRef: (n: number) => string;
  // Live validity of the two band buffers, for visible field feedback.
  belowInvalid: boolean;
  aboveInvalid: boolean;
  // Quick-fill the goal from the 4-week average; undefined when no data.
  onUseAvg?: () => void;
  // AP-9 — quick-fill the goal from what the program prescribes; undefined when
  // there's no prescription for this target (non-muscle/hard-set, or unwired).
  onUsePrescribed?: () => void;
  onAxis: (axis: TargetAxis) => void;
  onMetric: (metric: TargetMetric) => void;
  onKey: (key: string) => void;
  onValueChange: (raw: string) => void;
  onValueBlur: () => void;
  onBelowChange: (raw: string) => void;
  onAboveChange: (raw: string) => void;
  onBandBlur: () => void;
  onCollapse: () => void;
};

export function WeeklyTargetEditorRow({
  theme,
  cs,
  ps,
  selectStyles,
  title,
  axis,
  axisChips,
  metricChips,
  keyValue,
  keyOptions,
  valueText,
  unitLabel,
  bandLabel,
  belowText,
  aboveText,
  scored,
  actual,
  prescribed,
  formatRef,
  belowInvalid,
  aboveInvalid,
  onUseAvg,
  onUsePrescribed,
  onAxis,
  onMetric,
  onKey,
  onValueChange,
  onValueBlur,
  onBelowChange,
  onAboveChange,
  onBandBlur,
  onCollapse,
}: Props) {
  const { t } = useTranslation();
  const p = theme.palette;
  const styles = makeStyles(theme);

  const keyFieldLabel =
    axis === "muscle"
      ? t("composer.targetKeyMuscle", { defaultValue: "Muscle" })
      : t("composer.targetKeyExercise", { defaultValue: "Exercise" });

  return (
    <View style={ps.editorFrame}>
      <View style={ps.editorHead}>
        <Text style={ps.editorTitle} numberOfLines={1}>
          {title}
        </Text>
        <Pressable
          accessibilityRole="button"
          accessibilityState={{ expanded: true }}
          accessibilityLabel={t("composer.targetCollapse", { defaultValue: "Collapse" })}
          onPress={onCollapse}
          hitSlop={8}
          style={ps.collapseBtn}
        >
          <Icon name="chevron-up" size={ps.collapseText.fontSize} color={ps.collapseText.color} />
        </Pressable>
      </View>

      <Text style={cs.fieldLabel}>{t("composer.targetAxis", { defaultValue: "Axis" })}</Text>
      <View style={styles.chipRow}>
        {axisChips.map((c) => (
          <Chip
            key={c.key}
            theme={theme}
            shape="box"
            label={c.label}
            selected={c.selected}
            disabled={c.disabled}
            onPress={() => onAxis(c.key)}
          />
        ))}
      </View>

      <Text style={cs.fieldLabel}>{t("composer.targetMetric", { defaultValue: "Metric" })}</Text>
      <View style={styles.chipRow}>
        {metricChips.map((c) => (
          <Chip
            key={c.key}
            theme={theme}
            shape="box"
            label={c.label}
            selected={c.selected}
            disabled={c.disabled}
            onPress={() => onMetric(c.key)}
          />
        ))}
      </View>

      <Text style={cs.fieldLabel}>{keyFieldLabel}</Text>
      <Select
        value={keyValue}
        options={keyOptions}
        onChange={onKey}
        searchable={keyOptions.length > 8}
        styles={selectStyles}
        accessibilityLabel={keyFieldLabel}
      />

      <Text style={cs.fieldLabel}>{t("composer.targetValue", { defaultValue: "Weekly goal" })}</Text>
      <View style={styles.valueRow}>
        <TextField
          theme={theme}
          numeric
          value={valueText}
          onChangeText={onValueChange}
          onEndEditing={onValueBlur}
          style={styles.valueInput}
          accessibilityLabel={t("composer.targetValue", { defaultValue: "Weekly goal" })}
        />
        <Text style={styles.unit}>{unitLabel}</Text>
      </View>

      {/* Per-target band (AP-1b-b): how far below/above the goal still counts
          as "on target". Two percentages; the resolved label mirrors the meter. */}
      <Text style={cs.fieldLabel}>
        {t("composer.targetBandLabel", { defaultValue: "Tolerance" })}
      </Text>
      <View style={styles.bandRow}>
        <View style={styles.bandField}>
          <Text style={styles.bandSign}>−</Text>
          <TextField
            theme={theme}
            numeric
            invalid={belowInvalid}
            hitSlop={8}
            value={belowText}
            onChangeText={onBelowChange}
            onEndEditing={onBandBlur}
            style={styles.bandInput}
            accessibilityLabel={t("composer.targetBandBelow", {
              defaultValue: "Percent below goal",
            })}
          />
          <Text style={styles.bandPct}>%</Text>
        </View>
        <View style={styles.bandField}>
          <Text style={styles.bandSign}>+</Text>
          <TextField
            theme={theme}
            numeric
            invalid={aboveInvalid}
            hitSlop={8}
            value={aboveText}
            onChangeText={onAboveChange}
            onEndEditing={onBandBlur}
            style={styles.bandInput}
            accessibilityLabel={t("composer.targetBandAbove", {
              defaultValue: "Percent above goal",
            })}
          />
          <Text style={styles.bandPct}>%</Text>
        </View>
        <Text style={styles.bandResolved}>{bandLabel}</Text>
      </View>
      {/* Paired visible error (TextField `invalid` is never colour-alone). */}
      {belowInvalid || aboveInvalid ? (
        <Text style={styles.bandError}>
          {t("composer.targetBandRange", {
            defaultValue: "Below 0–99%, above 0% or more.",
          })}
        </Text>
      ) : null}

      {/* Reference: where the athlete is now + what the program prescribes, so
          the goal is a nudge against both, not a guess. */}
      {actual || prescribed != null ? (
        <View style={styles.refBox}>
          {actual ? (
            actual.hasData ? (
            <>
              <View style={styles.refRow}>
                {/* PG-17: the average is over the 4 COMPLETE weeks, and this
                    week is said to be in progress. It used to read "last wk"
                    while showing the current, partial one. */}
                <Text style={styles.refText}>
                  {actual.hasFullWeek
                    ? t("composer.targetRefAvg", {
                        n: formatRef(actual.avgPerWeek),
                        defaultValue: "~{{n}}/wk avg",
                      })
                    : t("common.noFullWeekYet", {
                        defaultValue: "No full week yet",
                      })}
                  {"   ·   "}
                  {t("composer.targetRefThisWeek", {
                    n: formatRef(actual.thisWeek),
                    defaultValue: "this wk so far {{n}}",
                  })}
                </Text>
                {/* No complete week, no average to use: the button would seed a
                    saved goal from a number that does not exist. */}
                {onUseAvg && actual.hasFullWeek ? (
                  <Pressable
                    onPress={onUseAvg}
                    hitSlop={8}
                    style={styles.useAvgBtn}
                    accessibilityRole="button"
                    accessibilityLabel={t("composer.targetUseAvg", {
                      defaultValue: "Use average",
                    })}
                  >
                    <Text style={styles.useAvg}>
                      {t("composer.targetUseAvg", { defaultValue: "Use average" })}
                    </Text>
                  </Pressable>
                ) : null}
              </View>
              <MiniTrendBars
                theme={theme}
                weeks={[...actual.fullWeeks, actual.thisWeek]}
                partialLast
              />
            </>
            ) : (
              <Text style={styles.refNone}>
                {t("composer.targetRefNone", { defaultValue: "No data in the last 4 weeks" })}
              </Text>
            )
          ) : null}
          {prescribed != null ? (
            <View style={styles.refRow}>
              <Text style={styles.refProgram}>
                {t("composer.targetRefProgram", {
                  n: formatRef(prescribed),
                  defaultValue: "program prescribes ~{{n}}/wk",
                })}
              </Text>
              {onUsePrescribed ? (
                <Pressable
                  onPress={onUsePrescribed}
                  hitSlop={8}
                  style={styles.useAvgBtn}
                  accessibilityRole="button"
                  accessibilityLabel={t("composer.targetUseProgram", {
                    defaultValue: "Use program value",
                  })}
                >
                  <Text style={styles.useAvg}>
                    {t("composer.targetUseProgram", {
                      defaultValue: "Use program value",
                    })}
                  </Text>
                </Pressable>
              ) : null}
            </View>
          ) : null}
        </View>
      ) : null}

      {!scored ? (
        <View style={styles.noteRow}>
          <Icon name="info" size={theme.typography.sizes.sm} color={p.ink3} />
          <Text style={styles.noteText}>
            {t("composer.targetNotScoredNote", {
              defaultValue: "This target type isn't scored by the level meter yet.",
            })}
          </Text>
        </View>
      ) : null}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: t } = theme;
  return StyleSheet.create({
    chipRow: { flexDirection: "row", flexWrap: "wrap", gap: s.s2 },
    valueRow: { flexDirection: "row", alignItems: "center", gap: s.s3 },
    valueInput: { width: 120 },
    unit: { color: p.ink3, fontSize: t.sizes.sm },
    bandRow: {
      flexDirection: "row",
      alignItems: "center",
      flexWrap: "wrap",
      gap: s.s3,
    },
    bandField: { flexDirection: "row", alignItems: "center", gap: s.s2 },
    bandSign: {
      color: p.ink2,
      fontSize: t.sizes.base,
      fontWeight: "700",
      fontVariant: ["tabular-nums"],
    },
    bandInput: { width: 64 },
    bandPct: { color: p.ink3, fontSize: t.sizes.sm },
    bandResolved: { color: p.ink3, fontSize: t.sizes.sm, fontVariant: ["tabular-nums"] },
    bandError: { color: p.danger, fontSize: t.sizes.sm },
    refBox: {
      backgroundColor: p.surface2,
      borderRadius: r.r2,
      paddingHorizontal: s.s3,
      paddingVertical: s.s2,
      gap: s.s2,
    },
    refRow: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      rowGap: s.s2,
      gap: s.s3,
    },
    refText: {
      color: p.ink2,
      // base, not sm: this is the number that turns a goal into a nudge — it
      // must not read as equal-weight with the disposable unit/band labels.
      fontSize: t.sizes.base,
      fontWeight: "600",
      fontVariant: ["tabular-nums"],
      flexShrink: 1,
    },
    // Secondary outline button (DESIGN §Buttons): borderStrong + ink label, so
    // the CTA clears ≥44 (36 box + hitSlop) and never depends on brand-on-
    // surface2 contrast (2.37:1 in Rosé Pine Dawn).
    useAvgBtn: {
      // 36 + hitSlop reaches 44 natively, but RNW drops `hitSlop` (DS-48), so
      // on web the box itself carries the floor. Same idiom as <Chip>.
      minHeight: webTapFloor ?? 36,
      paddingHorizontal: s.s3,
      justifyContent: "center",
      borderWidth: 1,
      borderColor: p.borderStrong,
      borderRadius: r.r2,
    },
    useAvg: { color: p.ink2, fontSize: t.sizes.sm, fontWeight: "700" },
    refNone: { color: p.ink3, fontSize: t.sizes.sm },
    // The program's ask — supplementary to the actual (ink3/sm), so it reads as
    // context under the athlete's own number, not competing with it.
    refProgram: { color: p.ink3, fontSize: t.sizes.sm, fontVariant: ["tabular-nums"] },
    noteRow: { flexDirection: "row", alignItems: "center", gap: 6 },
    noteText: { color: p.ink3, fontSize: t.sizes.sm, flex: 1 },
  });
}
